import type { Command, CommandResult, WorldSnapshot } from "@radar-sim/schema";
import type { Recording } from "./recording.js";
import type {
  ConnectionState,
  RadarStateSource,
  Unsubscribe,
} from "./state-source.js";

export interface ReplaySourceOptions {
  /**
   * Replay rate multiplier. 1 = real time, 2 = 2x speed, 0 = paused.
   * Mutated at runtime via `setRate(...)` (replay UI commonly exposes a
   * scrubber and speed control).
   */
  rate?: number;
  /**
   * If false, `connect()` emits the first snapshot but does not start an
   * interval-driven loop; the consumer drives advance via `step()`. Default
   * true. Used by tests and by debrief UIs that own their own clock.
   */
  autoTick?: boolean;
}

const DEFAULT_RATE = 1;

/**
 * Reads a captured `Recording` and emits its snapshots back to subscribers
 * at a configurable rate. Same `RadarStateSource` contract as
 * `LocalSimSource` — the PPI engine, MARPA tracker, etc. consume identical
 * snapshot streams in both modes.
 *
 * Commands other than pause/resume/set_time_scale are rejected with
 * `rejected_by_source` because a recorded world cannot be mutated mid-replay.
 */
export class ReplaySource implements RadarStateSource {
  readonly mode = "replay" as const;
  readonly tickRateHz: number;

  private readonly recording: Recording;
  private readonly autoTick: boolean;
  private rate: number;

  private connectionState: ConnectionState = "idle";
  private cursor = 0;

  private intervalHandle: ReturnType<typeof setInterval> | undefined;

  private readonly snapshotHandlers = new Set<(s: WorldSnapshot) => void>();
  private readonly stateHandlers = new Set<(s: ConnectionState) => void>();

  constructor(recording: Recording, opts: ReplaySourceOptions = {}) {
    this.recording = recording;
    this.tickRateHz = recording.metadata.tickRateHz;
    this.autoTick = opts.autoTick ?? true;
    this.rate = opts.rate ?? DEFAULT_RATE;
  }

  get state(): ConnectionState {
    return this.connectionState;
  }

  get cursorIndex(): number {
    return this.cursor;
  }

  get snapshotCount(): number {
    return this.recording.snapshots.length;
  }

  setRate(rate: number): void {
    this.rate = rate;
  }

  /** Seek to a specific snapshot index. Clamped to the recording's range. */
  seek(index: number): void {
    this.cursor = Math.max(0, Math.min(index, this.recording.snapshots.length - 1));
    const snap = this.recording.snapshots[this.cursor];
    if (snap !== undefined) this.publish(snap);
  }

  async connect(): Promise<void> {
    if (this.connectionState === "active") return;
    this.transitionState("active");
    if (this.recording.snapshots.length > 0) {
      const first = this.recording.snapshots[0]!;
      this.publish(first);
    }
    if (this.autoTick) {
      const intervalMs = 1000 / this.tickRateHz;
      this.intervalHandle = setInterval(() => this.tick(), intervalMs);
    }
  }

  async disconnect(): Promise<void> {
    if (this.intervalHandle !== undefined) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = undefined;
    }
    this.transitionState("disconnected");
  }

  subscribe(handler: (snapshot: WorldSnapshot) => void): Unsubscribe {
    this.snapshotHandlers.add(handler);
    return () => {
      this.snapshotHandlers.delete(handler);
    };
  }

  onStateChange(handler: (state: ConnectionState) => void): Unsubscribe {
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  async sendCommand(command: Command): Promise<CommandResult> {
    switch (command.kind) {
      case "pause":
        if (this.connectionState === "active") this.transitionState("paused");
        return { ok: true };
      case "resume":
        if (this.connectionState === "paused") this.transitionState("active");
        return { ok: true };
      case "set_time_scale":
        this.rate = command.scale;
        return { ok: true };
      default:
        return {
          ok: false,
          code: "rejected_by_source",
          reason: "replay sources cannot accept world-mutating commands",
        };
    }
  }

  /** Advance one snapshot. Public for tests and consumer-driven scrubbing. */
  step(): void {
    this.tick();
  }

  private tick(): void {
    if (this.connectionState !== "active") return;
    if (this.rate <= 0) return;
    // For non-unit rates, integer step counts aren't easily expressible per
    // wall tick; we step ceil(rate) snapshots per tick and discard everything
    // but the last (consumers see one snapshot per tick at scaled time).
    const stepsThisTick = Math.max(1, Math.round(this.rate));
    for (let i = 0; i < stepsThisTick; i++) {
      const next = this.cursor + 1;
      if (next >= this.recording.snapshots.length) {
        // End of recording: stop ticking but keep state active for
        // deterministic "end-of-recording" handling by consumers.
        if (this.intervalHandle !== undefined) {
          clearInterval(this.intervalHandle);
          this.intervalHandle = undefined;
        }
        return;
      }
      this.cursor = next;
    }
    const snap = this.recording.snapshots[this.cursor]!;
    this.publish(snap);
  }

  private publish(snap: WorldSnapshot): void {
    for (const handler of this.snapshotHandlers) handler(snap);
  }

  private transitionState(next: ConnectionState): void {
    if (this.connectionState === next) return;
    this.connectionState = next;
    for (const handler of this.stateHandlers) handler(next);
  }
}
